import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, ChevronRight, ChevronLeft, CheckCircle2, Circle, Minimize2, Maximize2, Navigation, MapPin } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

import getGuides from '../data/guidesSteps';
import useGuides from '../hooks/useGuides';
import { trackWalkthroughEvent } from '../utils/walkthroughAnalytics';

export { getGuides as GUIDES_FUNC };

export default function HelpGuide({ activeGuide, onClose }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [minimized, setMinimized] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const GUIDES = useGuides();
  const guide = GUIDES[activeGuide];
  const completedRef = useRef(false);
  const stepsViewedRef = useRef(new Set());
  useEffect(() => {
    setCurrentStep(0); setCompletedSteps(new Set()); setMinimized(false);
    completedRef.current = false; stepsViewedRef.current = new Set();
    if (activeGuide && guide) {
      trackWalkthroughEvent({
        guide_id: activeGuide, guide_title: guide.title,
        step_index: 0, total_steps: guide.steps.length, event: 'started',
      });
      stepsViewedRef.current.add(0);
    }
  }, [activeGuide, guide]);
  // Emit abandoned on unmount if not completed
  useEffect(() => () => {
    if (activeGuide && guide && !completedRef.current) {
      trackWalkthroughEvent({
        guide_id: activeGuide, guide_title: guide.title,
        step_index: currentStep, total_steps: guide.steps.length, event: 'abandoned',
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGuide]);
  const markComplete = useCallback((idx) => { setCompletedSteps(prev => new Set([...prev, idx])); }, []);
  const goToStep = useCallback((idx) => {
    if (!guide) return;
    const step = guide.steps[idx];
    setCurrentStep(idx);
    if (!stepsViewedRef.current.has(idx)) {
      stepsViewedRef.current.add(idx);
      trackWalkthroughEvent({
        guide_id: activeGuide, guide_title: guide.title,
        step_index: idx, total_steps: guide.steps.length, event: 'step_viewed',
      });
    }
    if (step.page && `${location.pathname}${location.search}` !== step.page && location.pathname !== step.page) navigate(step.page);
  }, [guide, navigate, location.pathname, location.search, activeGuide]);
  const handleNext = () => { markComplete(currentStep); if (currentStep < guide.steps.length - 1) goToStep(currentStep + 1); };
  const handlePrev = () => { if (currentStep > 0) goToStep(currentStep - 1); };
  const handleFinish = () => {
    if (guide && !completedRef.current) {
      completedRef.current = true;
      trackWalkthroughEvent({
        guide_id: activeGuide, guide_title: guide.title,
        step_index: guide.steps.length - 1, total_steps: guide.steps.length, event: 'completed',
      });
    }
    onClose();
  };
  if (!guide) return null;
  const step = guide.steps[currentStep];
  const progress = ((completedSteps.size) / guide.steps.length) * 100;
  const isLastStep = currentStep === guide.steps.length - 1;
  const allDone = completedSteps.size === guide.steps.length;
  if (minimized) return (
    <button data-testid="help-guide-minimized" onClick={() => setMinimized(false)} className="fixed bottom-20 right-6 z-50 flex items-center gap-2 px-4 py-2.5 rounded-full shadow-lg border border-sky-500/20 text-sm font-medium text-sky-400 hover:scale-105 transition-all" style={{ background: 'hsl(222 47% 10% / 0.95)', backdropFilter: 'blur(12px)' }}>
      <Navigation className="w-4 h-4" /><span style={{ fontFamily: 'Barlow Condensed' }}>{guide.title}</span><span className="text-[10px] text-muted-foreground ml-1">{currentStep + 1}/{guide.steps.length}</span><Maximize2 className="w-3.5 h-3.5 ml-1 text-muted-foreground" />
    </button>
  );
  return (
    <div data-testid="help-guide-panel" className="fixed bottom-20 right-6 z-50 w-[360px] rounded-xl shadow-2xl shadow-black/30 border border-sky-500/15 overflow-hidden" style={{ background: 'hsl(222 47% 8% / 0.97)', backdropFilter: 'blur(20px)' }}>
      <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
        <div className="flex items-center gap-2.5 min-w-0">
          <button onClick={onClose} className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0 hover:bg-sky-500/10 transition-colors" title="Back to Guides" data-testid="guide-back"><ChevronLeft className="w-3.5 h-3.5 text-sky-400" /></button>
          <div className="min-w-0"><h3 className="text-xs font-semibold uppercase tracking-wider text-sky-400 truncate" style={{ fontFamily: 'Barlow Condensed' }}>{guide.title}</h3><p className="text-[10px] text-muted-foreground">{completedSteps.size} of {guide.steps.length} steps</p></div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setMinimized(true)} className="p-1.5 rounded-lg hover:bg-white/5 text-muted-foreground hover:text-foreground transition-colors" title="Minimize"><Minimize2 className="w-3.5 h-3.5" /></button>
          <button data-testid="help-guide-close" onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5 text-muted-foreground hover:text-foreground transition-colors" title="Close"><X className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      <div className="h-1 bg-secondary/30"><div className="h-full bg-sky-500 transition-all duration-500" style={{ width: `${progress}%` }} /></div>
      <div className="max-h-[200px] overflow-y-auto px-3 py-2.5 space-y-0.5">
        {guide.steps.map((s, i) => { const done = completedSteps.has(i); const active = i === currentStep; const showPhase = s.phase && (i === 0 || guide.steps[i-1]?.phase !== s.phase); return (
          <React.Fragment key={`step-${i}`}>
            {showPhase && <div className="px-2.5 pt-2 pb-1"><span className="text-[9px] uppercase tracking-widest font-bold text-amber-400/80" style={{ fontFamily: 'Barlow Condensed' }}>{s.phase}</span></div>}
            <button data-testid={`guide-step-${i}`} onClick={() => goToStep(i)} className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left transition-all text-xs ${active ? 'bg-sky-500/10 text-sky-400' : done ? 'text-muted-foreground' : 'text-foreground/70 hover:bg-white/[0.03]'}`}>
              {done ? <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" /> : <Circle className={`w-4 h-4 flex-shrink-0 ${active ? 'text-sky-400' : 'text-muted-foreground/40'}`} />}
              <span className={`truncate ${done ? 'line-through opacity-60' : ''}`}>{s.label}</span>
              {active && <MapPin className="w-3 h-3 text-sky-400 ml-auto flex-shrink-0" />}
            </button>
          </React.Fragment>
        ); })}
      </div>
      <div className="px-4 py-3 border-t border-white/5 space-y-2">
        <p className="text-xs text-foreground/80 leading-relaxed">{step.instruction}</p>
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-sky-500/5 border border-sky-500/10"><ChevronRight className="w-3.5 h-3.5 text-sky-400 mt-0.5 flex-shrink-0" /><p className="text-xs text-sky-300/90 leading-relaxed">{step.action}</p></div>
      </div>
      <div className="px-4 py-3 border-t border-white/5 flex items-center justify-between">
        <button data-testid="guide-prev" onClick={handlePrev} disabled={currentStep === 0} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"><ChevronLeft className="w-3.5 h-3.5" /> Back</button>
        {allDone ? (
          <button data-testid="guide-finish" onClick={handleFinish} className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium rounded-lg bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 transition-colors"><CheckCircle2 className="w-3.5 h-3.5" /> Done</button>
        ) : (
          <button data-testid="guide-next" onClick={isLastStep ? handleFinish : handleNext} className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium rounded-lg bg-sky-500/20 text-sky-400 hover:bg-sky-500/30 transition-colors">{isLastStep ? 'Finish' : 'Next'} <ChevronRight className="w-3.5 h-3.5" /></button>
        )}
      </div>
    </div>
  );
}
